"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputPassword } from "@/components/ui/input-password";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReloadIcon } from "@radix-ui/react-icons";
import { LucideKey, LucideMail } from "lucide-react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";
import { authClient } from "@/lib/auth-client";
import { sanitizeRedirectUrl } from "@/lib/safe-redirect";

const formSchema = z.object({
  email: z.string().email(),
  password: z.string(),
});

export default function LoginPage() {
  const searchParams = useSearchParams();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      const redirectTo = sanitizeRedirectUrl(searchParams.get("callbackUrl"), window.location.origin);

      const result = await authClient.signIn.email({
        email: values.email,
        password: values.password,
        rememberMe: true,
        callbackURL: redirectTo,
      });

      if (result.error || !result.data) {
        form.setError("password", { message: result.error.message || "An error occurred" });
        return;
      }

      if ("twoFactorRedirect" in result.data && result.data.twoFactorRedirect) {
        // onTwoFactorRedirect in auth-client handles navigation to /auth/verify-2fa
        return;
      }

      window.location.assign(redirectTo);
    },
    [searchParams, form]
  );

  const isLogginIn = useMemo(() => {
    return form.formState.isSubmitting || form.formState.isSubmitSuccessful;
  }, [form.formState.isSubmitting, form.formState.isSubmitSuccessful]);

  return (
    <div className="w-full flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Login</CardTitle>
          <CardDescription>Enter your email below to login to your account.</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          id="email"
                          autoComplete="current-email"
                          autoFocus
                          onFocus={({ target }) => {}}
                          onChange={e => {
                            field.onChange(e);
                          }}
                          onBlur={() => {
                            field.onBlur();
                          }}
                          className="pl-8"
                        />
                        <div className="absolute left-2.5 top-2.5 text-muted-foreground text-sm">
                          <LucideMail className="size-4" />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <FormLabel htmlFor="password">Password</FormLabel>
                      <Link
                        href="/auth/forgot"
                        tabIndex={1}
                        className="ml-auto inline-block text-sm underline"
                        style={{ display: "none" }}
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <FormControl>
                      <div className="relative">
                        <InputPassword
                          {...field}
                          id="password"
                          autoComplete="current-password"
                          tabIndex={0}
                          onBlur={() => {
                            field.onBlur();
                          }}
                          onChange={e => {
                            field.onChange(e);
                          }}
                          className="pl-8"
                        />
                        <div className="absolute left-2.5 top-2.5 text-muted-foreground text-sm">
                          <LucideKey className="size-4" />
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={isLogginIn}>
                {isLogginIn && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
                Login
              </Button>
            </form>
          </Form>
          <div className="mt-4 text-center text-sm space-y-2">
            {/* <div className="pt-2">
              <Link href="/auth/magic" className="block">
                <Button variant="outline" className="w-full">
                  Sign in with Magic Link
                </Button>
              </Link>
            </div> */}
            {/* <div className="pt-2">
              Don&apos;t have an account?{" "}
              <Link href="/auth/register" className="underline">
                Sign up
              </Link>
            </div> */}
            <div className="pt-2">
              <Link href="/auth/forgot" className="underline">
                Forgot your password?
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
